import React, { useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import PlaceNameAutocomplete from './PlaceNameAutocomplete';
import PlaceProvinceSelect from './PlaceProvinceSelect';
import {
  EMPTY_LEAD_PROFILE_FILTERS,
  LEAD_PROFILE_AGE_BANDS,
  LEAD_PROFILE_FILTER_GROUPS,
  addLeadProfileZone,
  describeLeadProfileZone,
  leadProfileZoneKey,
} from '../leadFilters';
import { LEAD_PF_ZONE_MAX, LEAD_PF_ZONE_MAX_MESSAGE } from '../../services/leadsService';
import { LEADS_TEXT_MUTED, getLeadStageFilterChipSx, leadsSectionTitleSx } from './leadsUi.styles';

const titleSx = { ...leadsSectionTitleSx, mb: 0.75 };
const subTitleSx = { fontSize: '12px', color: LEADS_TEXT_MUTED, mb: 0.5 };
const rowSx = { display: 'flex', gap: 0.75, flexWrap: 'wrap' };
const ANY = '';
const OTHER = { kind: 'other' };
const NONE = { kind: 'none' };

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

/**
 * «Datos del cliente» en Filtros de Leads (`pf_*` de getleads, contrato
 * FRONTEND_LEAD_PROFILE.md § Filtros por perfil del cliente). Edad: una banda
 * (o «Cualquiera»). Resto: multi-selección con «Sin dato» (`none`). Zona:
 * provincia (acota) + buscador del catálogo; cada lugar elegido es un chip;
 * «Otro» (texto libre) y «Sin dato» como chips fijos. Hasta 20 zonas.
 *
 * @param {{ value: object, onChange: (next: object) => void }} props
 */
export default function LeadsProfileFiltersSection({ value, onChange }) {
  const v = { ...EMPTY_LEAD_PROFILE_FILTERS, ...(value || {}) };
  const set = (patch) => onChange({ ...v, ...patch });
  const [province, setProvince] = useState('');
  const zoneKeys = v.zones.map(leadProfileZoneKey);
  const full = v.zones.length >= LEAD_PF_ZONE_MAX;
  const catalogZones = v.zones.filter((z) => z?.kind !== 'other' && z?.kind !== 'none');
  const toggleFixedZone = (zone) => {
    const key = leadProfileZoneKey(zone);
    set({ zones: zoneKeys.includes(key) ? v.zones.filter((z) => leadProfileZoneKey(z) !== key) : addLeadProfileZone(v.zones, zone) });
  };
  const removeZone = (key) => set({ zones: v.zones.filter((z) => leadProfileZoneKey(z) !== key) });

  return (
    <Box role="group" aria-label="Datos del cliente" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography sx={{ ...titleSx, mb: 0 }}>Datos del cliente</Typography>

      <Box>
        <Typography sx={subTitleSx}>Edad</Typography>
        <Box role="group" aria-label="Edad" sx={rowSx}>
          {[{ value: ANY, label: 'Cualquiera' }, ...LEAD_PROFILE_AGE_BANDS].map((b) => (
            <Button
              key={b.value || 'any'}
              size="small"
              aria-pressed={v.age === b.value}
              onClick={() => set({ age: b.value })}
              sx={getLeadStageFilterChipSx(v.age === b.value)}
            >
              {b.label}
            </Button>
          ))}
        </Box>
      </Box>

      {LEAD_PROFILE_FILTER_GROUPS.map((g) => (
        <Box key={g.key}>
          <Typography sx={subTitleSx}>{g.label}</Typography>
          <Box role="group" aria-label={g.label} sx={rowSx}>
            {g.options.map((o) => {
              const on = v[g.key].includes(o.value);
              return (
                <Button
                  key={o.value}
                  size="small"
                  aria-pressed={on}
                  onClick={() => set({ [g.key]: toggle(v[g.key], o.value) })}
                  sx={getLeadStageFilterChipSx(on)}
                >
                  {o.label}
                </Button>
              );
            })}
          </Box>
        </Box>
      ))}

      <Box>
        <Typography sx={subTitleSx}>Zona donde vive</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <PlaceProvinceSelect value={province} onChange={setProvince} disabled={full} label="Provincia" ariaLabel="Provincia de la zona" />
          <PlaceNameAutocomplete
            province={province}
            disabled={full}
            allowOther={false}
            ariaLabel="Buscar zona del cliente"
            placeholder="Buscar barrio, ciudad o provincia"
            onPick={(picked) => set({ zones: addLeadProfileZone(v.zones, picked) })}
          />
          <Box role="group" aria-label="Zona sin catálogo" sx={rowSx}>
            {[{ zone: OTHER, label: 'Otro' }, { zone: NONE, label: 'Sin dato' }].map(({ zone, label }) => {
              const on = zoneKeys.includes(zone.kind);
              return (
                <Button
                  key={zone.kind}
                  size="small"
                  aria-pressed={on}
                  disabled={full && !on}
                  onClick={() => toggleFixedZone(zone)}
                  sx={getLeadStageFilterChipSx(on)}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
          {catalogZones.length > 0 && (
            <Box role="list" aria-label="Zonas elegidas" sx={rowSx}>
              {catalogZones.map((z) => {
                const key = leadProfileZoneKey(z);
                const text = describeLeadProfileZone(z);
                return (
                  <Chip
                    key={key}
                    role="listitem"
                    size="small"
                    label={text}
                    title={z.label || text}
                    onDelete={() => removeZone(key)}
                    deleteIcon={<span aria-label={`Quitar ${text}`} role="button">×</span>}
                    sx={{ bgcolor: 'rgba(72,146,145,0.22)', color: 'inherit', maxWidth: '100%' }}
                  />
                );
              })}
            </Box>
          )}
          {full && <Typography sx={{ fontSize: '11px', color: LEADS_TEXT_MUTED }}>{LEAD_PF_ZONE_MAX_MESSAGE}</Typography>}
        </Box>
      </Box>
    </Box>
  );
}
